import { Component, OnInit, AfterViewInit, ElementRef, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';

type RecurrenceUnit = 'day' | 'week' | 'month' | 'year';

export interface RecurrenceRuleResult {
  startDate: Date | null;
  startTime: Date | null;
  unit: RecurrenceUnit;
  daysOfWeek: number[]; // 1=Mon ... 7=Sun
  endDate: Date | null; // null means no end date
  dayOfMonth?: number | null;
}

export interface RecurrencePopupData {
  startDate?: Date;
  startTime?: Date;
  unit?: RecurrenceUnit;
  daysOfWeek?: number[];
  endDate?: Date;
  dayOfMonth?: number;
  customActionPayload?: any; // The entire payload from custom action table
  orgProcessId?: string; // org_process_id
  schedulerLabel?: string; // The scheduler label from the custom action form
}

export interface ScheduleSelectionPayload {
  schedule: {
    scheduler_name: string;
    scheduler_label: string;
    tenant_id: string;
    execution_time: string;
    start_date: string;
    frequency: string;
    frequency_day?: string[];
    end_date?: string;
  };
}

@Component({
  selector: 'app-recurrence-popup',
  templateUrl: './recurrence-popup.component.html',
  styleUrls: ['./recurrence-popup.component.scss'],
  providers: [DatePipe],
  standalone: false
})
export class RecurrencePopupComponent implements OnInit, AfterViewInit {
  readonly nzModalData: Partial<RecurrencePopupData> = inject(NZ_MODAL_DATA);

  form!: FormGroup;
  summaryText = '';
  submitted = false;
  @ViewChild('startDateRef', { read: ElementRef }) startDateEl?: ElementRef<HTMLElement>;
  @ViewChild('startTimeRef', { read: ElementRef }) startTimeEl?: ElementRef<HTMLElement>;
  @ViewChild('unitSelectRef', { read: ElementRef }) unitSelectEl?: ElementRef<HTMLElement>;


  // Ordered Sunday -> Saturday for chip display
  readonly weekdayLabels = [
    { label: 'Sun', value: 7 }, // Sunday
    { label: 'Mon', value: 1 },
    { label: 'Tue', value: 2 },
    { label: 'Wed', value: 3 },
    { label: 'Thu', value: 4 },
    { label: 'Fri', value: 5 },
    { label: 'Sat', value: 6 },
  ];

  readonly unitOptions: { label: string; value: RecurrenceUnit }[] = [
    { label: 'Day', value: 'day' },
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
    { label: 'Year', value: 'year' },
  ];

  private modal = inject(NzModalRef<ScheduleSelectionPayload | 'cancel'>);
  private fb = inject(FormBuilder);
  private datePipe = inject(DatePipe);

  ngOnInit(): void {
    const initialStart = this.nzModalData.startDate ? new Date(this.nzModalData.startDate) : new Date();
    const initialUnit: RecurrenceUnit = (this.nzModalData.unit as RecurrenceUnit) || 'week';
    const initialDays = this.nzModalData.daysOfWeek || (initialUnit === 'day' ? [1,2,3,4,5,6,7] : this.inferDefaultDays(initialStart));
    const initialEnd = this.nzModalData.endDate ? new Date(this.nzModalData.endDate) : null;
    const initialDayOfMonth = (this.nzModalData as any).dayOfMonth ?? initialStart.getDate();

    this.form = this.fb.group({
      startDate: [initialStart, Validators.required],
      startTime: [initialStart, Validators.required],
      unit: [initialUnit, Validators.required],
      daysOfWeek: [initialDays],
      endDate: [initialEnd],
      dayOfMonth: [initialDayOfMonth, [Validators.min(1), Validators.max(31)]],
      schedulerLabel: [this.nzModalData.schedulerLabel || '', Validators.required],
    });

    this.form.valueChanges.subscribe(() => this.updateSummary());
    // Ensure end date never falls before start date
    this.form.get('startDate')?.valueChanges.subscribe((d: Date) => {
      const end = this.form.value.endDate as Date | null;
      if (end && this.startOfDay(end) < this.startOfDay(d)) {
        this.form.patchValue({ endDate: d });
      }
    });
    this.form.get('unit')?.valueChanges.subscribe((unit: RecurrenceUnit) => {
      const start: Date = this.form.value.startDate || new Date();
      if (unit === 'day') {
        // Day = all weekdays selected
        this.form.patchValue({ daysOfWeek: [1,2,3,4,5,6,7] }, { emitEvent: false });
      } else if (unit === 'week') {
        // For week, keep current selection if present; otherwise default to start day
        const current = this.form.value.daysOfWeek as number[] | undefined;
        if (!current || current.length === 0) {
          this.form.patchValue({ daysOfWeek: this.inferDefaultDays(start) }, { emitEvent: false });
        }
      }
      this.updateSummary();
    });
    this.updateSummary();
  }

  ngAfterViewInit(): void {
    // Match widths of time picker and frequency select to the date picker width
    try {
      const dateWidth = this.startDateEl?.nativeElement.getBoundingClientRect().width || 0;
      if (dateWidth > 0) {
        if (this.startTimeEl?.nativeElement) {
          (this.startTimeEl.nativeElement as HTMLElement).style.width = `${dateWidth}px`;
        }
        if (this.unitSelectEl?.nativeElement) {
          (this.unitSelectEl.nativeElement as HTMLElement).style.width = `${dateWidth}px`;
        }
      }
    } catch {}
  }

  toggleWeekday(day: number): void {
    const unit: RecurrenceUnit = this.form.value.unit;
    const selected: number[] = [...(this.form.value.daysOfWeek || [])];
    const idx = selected.indexOf(day);
    if (idx >= 0) {
      selected.splice(idx, 1);
    } else {
      selected.push(day);
    }
    selected.sort((a, b) => a - b);
    this.form.patchValue({ daysOfWeek: selected });

    // Auto-switch between day/week based on completeness of selection
    if (unit === 'day' && selected.length < 7) {
      this.form.patchValue({ unit: 'week' });
    } else if (unit === 'week' && selected.length === 7) {
      this.form.patchValue({ unit: 'day' });
    }
  }

  isWeekdaySelected(day: number): boolean {
    return (this.form.value.daysOfWeek || []).includes(day);
  }

  clearEndDate(): void {
    this.form.patchValue({ endDate: null });
  }

  save(): void {
    this.submitted = true;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.value as RecurrenceRuleResult;
    if (value.unit === 'week') {
      if (!value.daysOfWeek || value.daysOfWeek.length === 0) {
        value.daysOfWeek = this.inferDefaultDays(value.startDate || new Date());
      }
      value.dayOfMonth = null;
    } else if (value.unit === 'day') {
      if (!value.daysOfWeek || value.daysOfWeek.length === 0) {
        value.daysOfWeek = [1,2,3,4,5,6,7];
      }
      value.dayOfMonth = null;
    }

    // Generate the API payload structure
    const schedulePayload = this.generateSchedulePayload(value);
    this.modal.destroy(schedulePayload);
  }

  private generateSchedulePayload(value: RecurrenceRuleResult): ScheduleSelectionPayload {
    const startDate = value.startDate || new Date();
    const startTime = value.startTime || new Date();
    const executionTime = this.getExecutionTime(startTime);
    const frequency = this.getFrequency(value.unit);
    const frequencyDay = this.getFrequencyDay(value.unit, value.daysOfWeek);
    const schedulerLabel = this.form.get('schedulerLabel')?.value || "";

    const schedule: any = {
      scheduler_name: "eru_pg_cron_scheduler",
      scheduler_label: schedulerLabel,
      tenant_id: this.nzModalData.orgProcessId || "",
      execution_time: executionTime,
      start_date: this.formatDate(startDate),
      frequency: frequency,
    };
    if (frequencyDay && (frequency === 'daily' || frequency === 'weekly')) {
      schedule.frequency_day = frequencyDay;
    }
    if (value.endDate) {
      schedule.end_date = this.formatDate(value.endDate);
    }

    return { schedule };
  }

  private getExecutionTime(date: Date): string {
    // Format as UTC HH:mm
    return date.toISOString().substr(11, 5);
  }

  private getFrequency(unit: RecurrenceUnit): string {
    switch (unit) {
      case 'day': return 'daily';
      case 'week': return 'weekly';
      case 'month': return 'monthly';
      case 'year': return 'yearly';
      default: return 'weekly';
    }
  }

  private getFrequencyDay(unit: RecurrenceUnit, daysOfWeek?: number[]): string[] | undefined {
    if (unit === 'day' || unit === 'week') {
      return daysOfWeek?.map(day => this.weekdayFullName(day).toLowerCase()) || [];
    }
    return undefined;
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  }

  cancel(): void {
    this.modal.destroy('cancel');
  }

  private inferDefaultDays(date: Date): number[] {
    // Convert JS Sunday(0) -> 7, Monday(1) -> 1 ... Sunday -> 7
    const jsDay = date.getDay();
    const isoDay = jsDay === 0 ? 7 : jsDay; // 1..7
    return [isoDay];
  }

  private updateSummary(): void {
    const { unit, daysOfWeek, endDate, dayOfMonth } = this.form.value as RecurrenceRuleResult;

    let occursText = 'Occurs ';
    occursText += `every ${unit}`;

    if (unit === 'week' && daysOfWeek && daysOfWeek.length > 0) {
      const dayNames = daysOfWeek.map((d) => this.weekdayName(d));
      occursText += ` on ${this.humanJoin(dayNames)}`;
    }
    // monthly/yearly removed

    if (endDate) {
      occursText += ` until ${this.datePipe.transform(endDate, 'MMM d, y')}`;
    } else if (unit === 'day') {
      occursText += ' until';
    }

    this.summaryText = occursText;
  }

  private weekdayName(iso: number): string {
    switch (iso) {
      case 1: return 'Mon';
      case 2: return 'Tue';
      case 3: return 'Wed';
      case 4: return 'Thu';
      case 5: return 'Fri';
      case 6: return 'Sat';
      case 7: return 'Sun';
      default: return '';
    }
  }

  private weekdayFullName(iso: number): string {
    switch (iso) {
      case 1: return 'Monday';
      case 2: return 'Tuesday';
      case 3: return 'Wednesday';
      case 4: return 'Thursday';
      case 5: return 'Friday';
      case 6: return 'Saturday';
      case 7: return 'Sunday';
      default: return '';
    }
  }

  private humanJoin(parts: string[]): string {
    if (parts.length <= 1) return parts.join('');
    if (parts.length === 2) return parts.join(' and ');
    return parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
  }

  private monthName(m: number): string {
    const names = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    return names[m-1] || '';
  }

  // Disable past dates for start and end date pickers
  disableStartDate = (date: Date): boolean => {
    if (!date) return false;
    return this.startOfDay(date) < this.startOfDay(new Date());
  };

  disableEndDate = (date: Date): boolean => {
    if (!date) return false;
    const today = this.startOfDay(new Date());
    const start = this.startOfDay(this.form?.value?.startDate || new Date());
    const d = this.startOfDay(date);
    return d < today || d < start;
  };

  private startOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }
}


